/**
 * useShellRosterPush - the effects that keep the shared session in step with this client.
 *
 * - PUSH ROSTER CHANGES, declaratively rather than threaded through every
 *   mutation: the sync hook's echo prevention decides what needs writing,
 *   so re-offering every combatant is cheap. Never before this client has
 *   adopted the SHARED roster - a joining player's placeholder carries the
 *   GM's `tab-1` id. A combatant who left takes their document AND their
 *   marker with them: a kept token was resurrected when the id returned
 *   (a re-imported session file), and deployment then skipped them.
 * - ONE-SHOT SEED for houseRules.roundCountdown (v7.10.0): a session whose
 *   combatants carry the retired per-combatant flag (or a demo asking for
 *   it) gets the rule turned on once, by the GM, only while the key is
 *   absent - so a GM who turns it back off is not overruled.
 *
 * (The log's scroll left for the log itself in v9.7.0: hooks/useLogRead.)
 *
 * Extracted from DeathWatchSimulator; same effects, same order.
 */
import { useEffect, useRef } from 'react';
import * as multiplayer from '../multiplayer';

/** Should the GM seed the round countdown now? */
export function wantsCountdownSeed({ sessionId, isGm, rosterHydrated, permissions, roster, demoSeed }) {
  if (!sessionId || !isGm || !rosterHydrated) return false;
  if (permissions?.houseRules?.roundCountdown !== undefined) return false;
  return !!(demoSeed || roster.some((c) => c.state?.roundCountdown));
}

/** Who was on the roster before and is not now. */
const departed = (before, now) => before.filter((gone) => !now.some((c) => c.id === gone.id));

/** Those who left this roster: off the wire and the map, unless this screen only HEARD they left. */
function takeOff(departures, { removeCombatant, removeToken }) {
  for (const gone of departures) {
    // False = heard, not made (useCombatantSync): whoever took them off deleted their token too,
    // and sending that again, late, would take a re-staged one's.
    if (removeCombatant(gone.id) !== false) removeToken(gone.id);
  }
}

/** Offer every combatant to the sync, and take those who left ON THIS SCREEN off the wire and the map. */
function useRosterPush(p) {
  const { roster, sessionId, rosterHydrated } = p;
  // The verbs as of this render: the pass is driven by the roster, not by a verb's new identity.
  const verbs = useRef(p);
  verbs.current = p;
  const prevRosterRef = useRef(roster);
  useEffect(() => {
    if (!sessionId || !rosterHydrated) return;
    const { syncCombatantState, syncCombatantSheet, forgetHeard } = verbs.current;
    for (const c of roster) {
      syncCombatantState(c);
      syncCombatantSheet(c);
    }
    takeOff(departed(prevRosterRef.current, roster), verbs.current);
    // Whoever is here now was not a departure: a hearing about one of them is stale (useCombatantSync).
    forgetHeard?.(roster.map((c) => c.id));
    prevRosterRef.current = roster;
  }, [roster, sessionId, rosterHydrated]);
}

/** The countdown seed, at most once per mount. */
function useCountdownSeed({ sessionId, isGm, rosterHydrated, permissions, roster, demoSeed }) {
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!wantsCountdownSeed({ sessionId, isGm, rosterHydrated, permissions, roster, demoSeed })) return;
    seededRef.current = true;
    multiplayer.writeHouseRule(sessionId, permissions, 'roundCountdown', true).catch(console.error);
  }, [sessionId, isGm, rosterHydrated, permissions, roster, demoSeed]);
}

/**
 * @param {object} args - roster, sessionId, isGm, rosterHydrated,
 *   permissions, demoSeed (a demo scenario's roundCountdown wish),
 *   syncCombatantState, syncCombatantSheet, removeCombatant, removeToken,
 *   forgetHeard.
 */
export default function useShellRosterPush(args) {
  useRosterPush(args);
  useCountdownSeed(args);
}
